/**
 * A tela de Indicadores e Compliance (`O-11`) e o drill-down para a operação (`O-15`).
 *
 * **Todo indicador aqui abre a fila da outra aba já filtrada, com exatamente aquela
 * contagem.** É o `AC14` do cronograma, e sem ele esta tela é um pôster: números bonitos
 * que ninguém consegue conferir nem transformar em trabalho. Quem garante o "exatamente" é
 * o servidor — a régua e a fila contam sobre os mesmos recortes nomeados, e há teste que
 * percorre os seis conferindo um contra o outro. A tela só precisa **não inventar um
 * sétimo**: o recorte que ela manda na URL é o que a API devolveu, nunca um construído
 * aqui a partir do rótulo.
 *
 * **O que não existe aparece como ausência, e não como zero.** Dois dos seis indicadores
 * são em reais, e a plataforma não sabe converter trânsito em dinheiro enquanto não houver
 * tabela de tarifas (`R-12`, decisão de negócio nº 5). Um terceiro depende da saga da
 * `R-07`. Os três mostram a contagem que é verdadeira e dizem, no próprio cartão, de que
 * decisão o resto depende. `R$ 0,00` numa tela chamada *Compliance* seria afirmar que o
 * cliente não tem exposição financeira nenhuma — que é o oposto do que ela existe para
 * mostrar.
 */
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useNavigate } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { ContextoDaSessao } from "../api/contexto-sessao";
import { comoParametros } from "../api/tipos";
import type {
  FiltroDoPeriodo,
  Indicador,
  NoPortico,
  OpcaoDeFiltro,
  ReguaGerencial,
} from "../api/tipos";
import { BarrasComLinha, Ranking, Rosca } from "../componentes/graficos";
import {
  Badge,
  Botao,
  Cartao,
  Estado,
  Linha,
  Painel,
} from "../componentes/base";
import { Icone } from "../componentes/icones";
import { supervisiona } from "../componentes/navegacao";
import { useCelular } from "../ganchos/useCelular";
import {
  dataEHora,
  numeroLongo,
  percentual,
  reais,
  reaisCompacto,
} from "../formato";
import { agoraDoInquilino, useDeslocamento } from "../relogio";
import { ClasseEconomicaNaRegua } from "./ClasseEconomica";

/** Os períodos que a régua oferece prontos. Dias, porque o prazo legal é contado em dias. */
const PERIODOS = [
  { chave: "7", rotulo: "7 dias" },
  { chave: "30", rotulo: "30 dias" },
  { chave: "90", rotulo: "90 dias" },
  { chave: "tudo", rotulo: "Tudo" },
] as const;

type ChaveDoPeriodo = (typeof PERIODOS)[number]["chave"];

function desdeDoPeriodo(chave: ChaveDoPeriodo): string | undefined {
  if (chave === "tudo") return undefined;
  // No tempo da organização (ADR-059): "últimos 7 dias" de uma demonstração com o relógio
  // avançado são os sete dias antes do agora dela.
  const d = agoraDoInquilino();
  d.setDate(d.getDate() - Number(chave));
  // Arredondado aos 10 minutos: é o que deixa o cache de dois minutos da API acertar quando
  // o apresentador volta do drill-down. Um `desde` ao milissegundo seria sempre outra chave.
  d.setMinutes(d.getMinutes() - (d.getMinutes() % 10), 0, 0);
  return d.toISOString();
}

export function Indicadores() {
  const [periodo, definirPeriodo] = useState<ChaveDoPeriodo>("30");
  const [concessao, definirConcessao] = useState("");
  const [praca, definirPraca] = useState("");
  const [regua, definirRegua] = useState<ReguaGerencial | null>(null);
  const [erro, definirErro] = useState<ErroDaApi | null>(null);
  const [atualizadoEm, definirAtualizadoEm] = useState<string | null>(null);
  const navegar = useNavigate();
  // Opcional, como no detalhe: a tela também é montada sem sessão (os testes da régua).
  const quem = useContext(ContextoDaSessao)?.quem;
  // O relógio da organização mudou (o F5, um avanço): o período recalcula (ADR-059).
  const deslocamento = useDeslocamento();

  const filtro = useMemo<FiltroDoPeriodo>(() => {
    void deslocamento;
    const desde = desdeDoPeriodo(periodo);
    return {
      ...(desde ? { desde } : {}),
      ...(concessao ? { concessao_id: concessao } : {}),
      ...(praca ? { praca_id: praca } : {}),
    };
  }, [periodo, concessao, praca, deslocamento]);

  const [recalculando, definirRecalculando] = useState(false);
  const carregar = useCallback(
    async (fresco = false) => {
      definirRecalculando(fresco);
      try {
        const encontrada = await api<ReguaGerencial>(
          `/tollvision/indicadores?${comoParametros(filtro, fresco ? { fresco: "true" } : {})}`,
        );
        definirRegua(encontrada);
        definirErro(null);
        // A hora do **cálculo**, e não a da resposta: com o cache, podem ser dois minutos.
        definirAtualizadoEm(
          new Date(encontrada.calculado_em ?? Date.now()).toLocaleTimeString(
            "pt-BR",
            {
              timeZone: "America/Sao_Paulo",
              hour: "2-digit",
              minute: "2-digit",
            },
          ),
        );
      } catch (falha) {
        definirErro(
          falha instanceof ErroDaApi
            ? falha
            : new ErroDaApi("Falha inesperada.", 0),
        );
        definirRegua(null);
      } finally {
        definirRecalculando(false);
      }
    },
    [filtro],
  );

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Trocar de concessão limpa a praça: a praça escolhida pode pertencer à concessão
   * anterior, e mantê-la faria a régua filtrar por um par que não existe — zero trânsitos,
   * sem nada na tela explicando por quê.
   */
  function escolherConcessao(id: string) {
    definirConcessao(id);
    definirPraca("");
  }

  /**
   * O drill-down (`O-15`): leva para a aba de operação com o recorte **e** os filtros do
   * período. Sem os filtros, a fila abriria o recorte sobre o tempo todo, e a contagem
   * deixaria de ser a que o usuário acabou de clicar.
   */
  function abrirRecorte(recorte: string, extras: Record<string, string> = {}) {
    if (!recorte) return;
    navegar(`/senatran?${comoParametros(filtro, { recorte, ...extras })}`);
  }

  function abrirNaOperacao(indicador: Indicador) {
    abrirRecorte(indicador.recorte);
  }

  /**
   * Clicar numa barra abre **aquele dia**, e não o período inteiro: o `desde`/`ate` do
   * filtro são substituídos pelo intervalo do dia. Sem isso, a barra levaria à mesma fila
   * que o cartão acima dela, e o gráfico deixaria de ser navegação para virar ilustração.
   */
  function abrirDia(dia: string) {
    const inicio = new Date(dia);
    const fim = new Date(inicio);
    fim.setDate(fim.getDate() + 1);
    navegar(
      `/senatran?${comoParametros(
        { ...filtro, desde: inicio.toISOString(), ate: fim.toISOString() },
        { recorte: "todos" },
      )}`,
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Indicadores e Compliance
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            Desempenho, falha e risco — e cada número abre a fila que o produziu
          </p>
        </div>
        {/* Os índices do regulador (`OB-05`): de todos que veem esta tela. */}
        <Link
          to="/indicadores/indices"
          title="Leitura de placa, processamento, disponível para pagar em 2 h e 24 h e disponibilidade dos pórticos, contra a meta do contrato ou a referência federal — por mês e por pórtico."
          className="ml-auto inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
        >
          <Icone.Alvo />
          Índices regulatórios
        </Link>
        {supervisiona(quem?.perfil) && (
          // O fechamento do período (`OB-06`): da supervisão, que entrega o pacote ao poder
          // concedente. Ação secundária da tela, à esquerda do "Calculado às".
          <Link
            to="/indicadores/fechamento"
            title="O mês ou o trimestre fechado para o poder concedente: resumo, compensação, fraudes com foto anonimizada e evasões, num ZIP."
            className="inline-flex min-h-8 items-center gap-1.5 rounded-botao border border-linha bg-painel-2 px-2.5 text-[11.5px] font-semibold text-tinta-2 hover:bg-realce"
          >
            <Icone.Baixar />
            Fechamento regulatório
          </Link>
        )}
        <button
          type="button"
          disabled={recalculando || !atualizadoEm}
          onClick={() => void carregar(true)}
          title="Conta de novo agora, sem o cache de dois minutos."
          className={`inline-flex items-center gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-[11px] text-tinta-3 hover:text-tinta-2 disabled:opacity-70`}
        >
          <Icone.Recarga />
          {recalculando
            ? "Recalculando…"
            : atualizadoEm
              ? `Calculado às ${atualizadoEm} · atualizar`
              : "Carregando…"}
        </button>
      </div>

      <Filtros
        periodo={periodo}
        aoTrocarPeriodo={definirPeriodo}
        concessao={concessao}
        aoTrocarConcessao={escolherConcessao}
        praca={praca}
        aoTrocarPraca={definirPraca}
        opcoes={regua}
      />

      {erro ? (
        <Painel titulo="Indicadores">
          <Estado
            tipo={erro.semLicenca ? "sem-licenca" : "erro"}
            titulo={
              erro.semLicenca
                ? "Esta organização não licenciou o módulo TollVision."
                : "Não foi possível calcular os indicadores."
            }
            detalhe={erro.message}
            acao={<Botao onClick={() => void carregar()}>Tentar de novo</Botao>}
          />
        </Painel>
      ) : !regua ? (
        <Painel titulo="Indicadores">
          <Estado tipo="carregando" titulo="Calculando os indicadores…" />
        </Painel>
      ) : (
        /* **Esta tela rola, e é a única do cockpit que rola.** A regra do §3 — "a página
            não rola, quem rola é o corpo de cada painel" — nasceu para a tela de operação,
            onde os painéis têm altura fixa e o conteúdo é uma lista. Aqui há régua *mais*
            quatro painéis, e num notebook de 1366×768 com a barra do navegador sobra menos
            de 400px para os quatro: fixar tudo não faz caber, faz sumir. Foi o que
            aconteceu — a faixa de gráficos aparecia como quatro títulos e nada embaixo,
            sem barra de rolagem que dissesse que havia mais.

            Então o título da tela e os filtros ficam parados (são eles que dizem *de que
            recorte* o número abaixo fala) e daqui para baixo rola tudo junto, numa barra
            só. `auto-rows-max` porque `auto` **não** é o que parece: numa grade de altura
            definida o Chrome reparte a altura igualmente entre as linhas `auto`, mesmo com
            `align-content: start`. */
        <div className="grid min-h-0 auto-rows-max content-start gap-3 overflow-y-auto pb-1">
          <Painel
            titulo="Régua gerencial"
            rodape={
              <span>
                Clique num indicador para abrir a fila com exatamente aquele
                recorte.
              </span>
            }
          >
            {/* **Seis, três ou dois por fileira — nunca cinco e um** (22/09: "fica 5 numa linha
                e 1 na de baixo"). O `auto-fit` deixava a sobra cair sozinha na segunda linha
                em qualquer tela entre 1.100 e 1.500 px, que é o notebook de quem apresenta.
                Colunas explícitas: 6 onde cabem 6, senão 3 + 3, senão 2 + 2 + 2. */}
            <ul
              aria-label="Indicadores"
              className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6"
            >
              {regua.indicadores.map((indicador) => (
                // O `li` é o contêiner da consulta de tamanho (`@container`), e não o cartão:
                // num botão flexível, `container-type` zera a largura intrínseca e o cartão
                // encolhe até a borda.
                <li key={indicador.chave} className="@container min-w-0">
                  <CartaoDoIndicador
                    indicador={indicador}
                    aoAbrir={() => abrirNaOperacao(indicador)}
                  />
                </li>
              ))}
            </ul>
          </Painel>

          {/* A classe econômica e a compensação estimada (`OB-01`): logo abaixo da régua,
              porque é dinheiro — e a ordem da tela é dinheiro primeiro. */}
          {regua.classe_economica && (
            <Painel
              titulo="Classe econômica e compensação"
              rodape={
                <span>
                  Válida, inválida ou fraudulenta, como o contrato define.
                  Clique numa classe para abrir a fila dela.
                </span>
              }
            >
              <ClasseEconomicaNaRegua
                classe={regua.classe_economica}
                aoAbrir={abrirRecorte}
              />
            </Painel>
          )}

          {/* Linhas em `max-content`: cada painel tem a altura que o conteúdo pede e quem
              rola é a coluna inteira, acima. Com altura de linha fixa, o ranking de quatro
              causas voltaria a ter barra própria dentro de um painel que já está dentro de
              uma área que rola — duas barras para o mesmo gesto. */}
          {/* Duas fileiras, como o mockup de 21/09: o volume no tempo largo ao lado da
              composição; embaixo, as perdas e o comparativo por concessionária. */}
          <div className="grid auto-rows-max gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <Painel
              titulo="Volume e exposição no tempo"
              rodape={
                <span>Clique numa barra para abrir a fila daquele dia.</span>
              }
            >
              <BarrasComLinha pontos={regua.serie} aoAbrirDia={abrirDia} />
            </Painel>

            <Painel titulo="Composição da arrecadação">
              <Rosca
                fatias={regua.composicao}
                aoAbrir={abrirRecorte}
                rotuloDoTotal="trânsitos com desfecho financeiro no período"
              />
            </Painel>
          </div>

          <div
            className={`grid auto-rows-max gap-3 ${
              regua.concessoes.length > 1
                ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]"
                : ""
            }`}
          >
            <Painel
              titulo="Top 5 motivos de perda"
              rodape={
                <span>
                  Cada linha é uma causa. O valor em reais depende da tabela de
                  tarifas (R-12).
                </span>
              }
            >
              <Ranking
                linhas={regua.perdas}
                aoAbrir={abrirRecorte}
                unidade="trânsitos"
              />
              <PerdidasNoPortico dado={regua.no_portico ?? null} />
            </Painel>

            {/* `O-14`: só para quem enxerga mais de uma concessão (ADR-009) — com uma, a
                tabela repetiria a régua acima numa linha só. */}
            {regua.concessoes.length > 1 && (
              <Painel
                titulo="Comparativo por concessionária"
                rodape={
                  <span>
                    Clique numa concessão para ver a tela inteira só dela.
                    Situação pelo SLA de 24 h: crítico abaixo de 95%, atenção
                    abaixo de 98%.
                  </span>
                }
              >
                <Comparativo
                  linhas={regua.comparativo}
                  aoEscolher={(id) => escolherConcessao(id)}
                />
              </Painel>
            )}
          </div>

          {/* Fora da fileira dos gráficos, e em largura cheia: este painel é o mais alto
              dos quatro (uma decisão pendente por cartão), e dentro da fileira ele definia
              a altura da linha — a rosca e as barras ganhavam 600px de vazio embaixo só
              para acompanhá-lo. */}
          <Painel titulo="O que esta tela ainda não diz">
            <Pendencias
              indicadores={regua.indicadores}
              nota={regua.perdas_que_faltam}
            />
          </Painel>
        </div>
      )}
    </>
  );
}

function Filtros({
  periodo,
  aoTrocarPeriodo,
  concessao,
  aoTrocarConcessao,
  praca,
  aoTrocarPraca,
  opcoes,
}: {
  periodo: ChaveDoPeriodo;
  aoTrocarPeriodo: (c: ChaveDoPeriodo) => void;
  concessao: string;
  aoTrocarConcessao: (id: string) => void;
  praca: string;
  aoTrocarPraca: (id: string) => void;
  opcoes: ReguaGerencial | null;
}) {
  const pracas = (opcoes?.pracas ?? []).filter(
    (p) => !concessao || p.concessao_id === concessao,
  );
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-cartao border border-linha bg-painel px-3 py-2.5">
      <div
        className="flex items-center gap-1"
        role="group"
        aria-label="Período"
      >
        {PERIODOS.map((p) => (
          <button
            key={p.chave}
            type="button"
            aria-pressed={periodo === p.chave}
            onClick={() => aoTrocarPeriodo(p.chave)}
            className={`min-h-8 rounded-botao border px-2.5 text-[11.5px] font-semibold transition-colors ${
              periodo === p.chave
                ? "border-azul-cheio bg-azul-cheio text-white"
                : "border-linha bg-painel-2 text-tinta-2 hover:bg-realce"
            }`}
          >
            {p.rotulo}
          </button>
        ))}
      </div>

      {/* As opções vêm da API, com o recorte de concessão do usuário já aplicado (ADR-009):
          oferecer no filtro uma praça que a fila vai recusar é oferecer um caminho para
          descobrir o que não se pode ver. */}
      <Escolha
        rotulo="Concessionária"
        valor={concessao}
        aoTrocar={aoTrocarConcessao}
        opcoes={opcoes?.concessoes ?? []}
        vazio="Todas"
      />
      <EscolhaDePraca
        valor={praca}
        aoTrocar={aoTrocarPraca}
        pracas={pracas}
        concessoes={opcoes?.concessoes ?? []}
        agrupar={!concessao}
      />
    </div>
  );
}

function Escolha({
  rotulo,
  valor,
  aoTrocar,
  opcoes,
  vazio,
}: {
  rotulo: string;
  valor: string;
  aoTrocar: (id: string) => void;
  opcoes: { id: string; nome: string }[];
  vazio: string;
}) {
  return (
    <label className="flex min-w-0 max-w-full items-center gap-1.5 text-[11px] text-tinta-3">
      {rotulo}
      <select
        value={valor}
        onChange={(e) => aoTrocar(e.target.value)}
        aria-label={rotulo}
        // `max-w-full` e `min-w-0`: o `<select>` dimensiona pela opção mais longa ("Rota da
        // Celulose — BR-…"), e numa tela de 390 px ele sozinho abria a página com zoom para
        // fora (`C-17`). Limitado, o texto da opção escolhida é cortado com reticências.
        className="min-h-8 min-w-0 max-w-full rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta-2"
      >
        <option value="">{vazio}</option>
        {opcoes.map((o) => (
          <option key={o.id} value={o.id}>
            {o.nome}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * A praça, agrupada pela concessionária a que pertence.
 *
 * Sem o agrupamento a lista é ambígua de um jeito que não se percebe: duas concessões da
 * mesma organização têm, com frequência, praças de mesmo nome (o "km 42" de cada uma), e o
 * `<select>` mostra quatro opções com dois rótulos repetidos. Quem escolher a errada não
 * recebe erro nenhum — recebe os números de outra rodovia.
 */
function EscolhaDePraca({
  valor,
  aoTrocar,
  pracas,
  concessoes,
  agrupar,
}: {
  valor: string;
  aoTrocar: (id: string) => void;
  pracas: OpcaoDeFiltro[];
  concessoes: OpcaoDeFiltro[];
  agrupar: boolean;
}) {
  if (!agrupar || concessoes.length < 2) {
    return (
      <Escolha
        rotulo="Praça"
        valor={valor}
        aoTrocar={aoTrocar}
        opcoes={pracas}
        vazio="Todas"
      />
    );
  }
  return (
    <label className="flex min-w-0 max-w-full items-center gap-1.5 text-[11px] text-tinta-3">
      Praça
      <select
        value={valor}
        onChange={(e) => aoTrocar(e.target.value)}
        aria-label="Praça"
        className="min-h-8 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta-2"
      >
        <option value="">Todas</option>
        {concessoes.map((c) => (
          <optgroup key={c.id} label={c.nome}>
            {pracas
              .filter((p) => p.concessao_id === c.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome}
                </option>
              ))}
          </optgroup>
        ))}
      </select>
    </label>
  );
}

const TOM: Record<string, string> = {
  neutro: "border-linha",
  atencao: "border-amarelo-borda bg-amarelo-fundo",
  critico: "border-vermelho-borda bg-vermelho-fundo",
};

/**
 * O cartão de um indicador — e o cartão **inteiro** é o botão.
 *
 * Era um cartão alto com um botão dentro, e os seis ocupavam 430px de altura: numa tela de
 * notebook isso é a régua sozinha empurrando os quatro gráficos para fora do campo de
 * visão. O alvo de clique ficou maior e a altura caiu pela metade, sem perder nada do que o
 * cartão dizia — o convite ("Abrir 7 na operação") continua escrito, porque é ele que
 * promete a contagem que a fila vai entregar (`AC14`).
 *
 * A linha de contexto é **uma** e tem `title`: o denominador quando o indicador é
 * percentual, a nota quando não é. O texto inteiro continua alcançável no `title` e, para
 * os bloqueados, no painel de pendências ao lado.
 */
const SITUACAO_DA_CONCESSAO: Record<
  string,
  { rotulo: string; cor: "verde" | "amarelo" | "vermelho" }
> = {
  saudavel: { rotulo: "Saudável", cor: "verde" },
  atencao: { rotulo: "Atenção", cor: "amarelo" },
  critico: { rotulo: "Crítico", cor: "vermelho" },
};

/**
 * O comparativo por concessionária (`O-14`): os números da régua recortados por concessão,
 * da mesma leitura — as linhas somam os cartões acima.
 */
function Comparativo({
  linhas,
  aoEscolher,
}: {
  linhas: ReguaGerencial["comparativo"];
  aoEscolher: (concessaoId: string) => void;
}) {
  const celular = useCelular();
  if (linhas.length === 0) {
    return (
      <p className="px-2 py-6 text-center text-[11.5px] text-tinta-3">
        Nenhum trânsito no período escolhido.
      </p>
    );
  }
  if (celular) {
    // No celular, uma concessão por cartão (`C-17` fase 2): sete colunas em 390 px
    // empilhavam o nome da concessão em seis linhas e escondiam as últimas colunas.
    return (
      <ul aria-label="Comparativo por concessionária" className="grid gap-1.5">
        {linhas.map((c) => {
          const situacao =
            SITUACAO_DA_CONCESSAO[c.situacao] ??
            SITUACAO_DA_CONCESSAO.saudavel!;
          return (
            <li key={c.concessao_id}>
              <button
                type="button"
                onClick={() => aoEscolher(c.concessao_id)}
                className="grid w-full gap-1.5 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2 text-left hover:bg-realce"
              >
                <span className="flex items-start gap-2">
                  <span className="min-w-0 flex-1 text-[12px] leading-snug font-semibold text-tinta">
                    {c.concessao}
                  </span>
                  <Badge cor={situacao.cor}>{situacao.rotulo}</Badge>
                </span>
                <dl className="grid grid-cols-3 gap-x-2 gap-y-1 text-[10.5px]">
                  {[
                    ["Trânsitos", numeroLongo(c.transitos)],
                    [
                      "Volume",
                      c.valor_centavos == null
                        ? "—"
                        : reaisCompacto(c.valor_centavos),
                    ],
                    ["Aceitação", percentual(c.taxa_de_aceitacao)],
                    ["SLA 24 h", percentual(c.sla_24h)],
                    ["Cancelados 7 d", numeroLongo(c.cancelados_por_prazo)],
                  ].map(([rotulo, valor]) => (
                    <span key={rotulo} className="grid">
                      <dt className="text-tinta-3">{rotulo}</dt>
                      <dd className="mono text-[12px] font-semibold text-tinta-2">
                        {valor}
                      </dd>
                    </span>
                  ))}
                </dl>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <div className="min-w-0 overflow-x-auto">
      <table
        aria-label="Comparativo por concessionária"
        className="w-full text-[11.5px]"
      >
        <thead>
          <tr className="text-left text-[10.5px] whitespace-nowrap text-tinta-3">
            <th className="py-1.5 pr-2 font-semibold">Concessão</th>
            <th className="py-1.5 pr-2 text-right font-semibold">Trânsitos</th>
            <th className="py-1.5 pr-2 text-right font-semibold">Volume</th>
            <th className="py-1.5 pr-2 text-right font-semibold">Aceitação</th>
            <th className="py-1.5 pr-2 text-right font-semibold">SLA 24 h</th>
            <th className="py-1.5 pr-2 text-right font-semibold">
              Cancelados 7 d
            </th>
            <th className="py-1.5 font-semibold">Situação</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((c) => {
            const situacao =
              SITUACAO_DA_CONCESSAO[c.situacao] ??
              SITUACAO_DA_CONCESSAO.saudavel!;
            return (
              <tr key={c.concessao_id} className="border-t border-linha-suave">
                <td className="py-1.5 pr-2">
                  <button
                    type="button"
                    onClick={() => aoEscolher(c.concessao_id)}
                    className="text-left leading-tight font-semibold text-tinta hover:text-azul"
                  >
                    {c.concessao}
                  </button>
                </td>
                <td className="mono py-1.5 pr-2 text-right whitespace-nowrap text-tinta-2">
                  {numeroLongo(c.transitos)}
                </td>
                <td className="mono py-1.5 pr-2 text-right whitespace-nowrap text-tinta-2">
                  {c.valor_centavos == null
                    ? "—"
                    : reaisCompacto(c.valor_centavos)}
                </td>
                <td className="mono py-1.5 pr-2 text-right whitespace-nowrap text-tinta-2">
                  {percentual(c.taxa_de_aceitacao)}
                </td>
                <td
                  className={`mono py-1.5 pr-2 text-right font-bold whitespace-nowrap ${
                    situacao.cor === "vermelho"
                      ? "text-vermelho"
                      : situacao.cor === "amarelo"
                        ? "text-amarelo"
                        : "text-verde"
                  }`}
                >
                  {percentual(c.sla_24h)}
                </td>
                <td className="mono py-1.5 pr-2 text-right whitespace-nowrap text-tinta-2">
                  {numeroLongo(c.cancelados_por_prazo)}
                </td>
                <td className="py-1.5">
                  <Badge cor={situacao.cor}>{situacao.rotulo}</Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const ICONE_DO_INDICADOR: Record<string, ReactNode> = {
  volume_financeiro: <Icone.Dinheiro />,
  exposicao_fora_do_sla: <Icone.Alerta />,
  taxa_de_aceitacao: <Icone.Certo />,
  sla_de_envio: <Icone.Relogio />,
  cancelados_por_prazo: <Icone.Barrado />,
  reclassificacoes_tag: <Icone.Recarga />,
};

function CartaoDoIndicador({
  indicador,
  aoAbrir,
}: {
  indicador: Indicador;
  aoAbrir: () => void;
}) {
  // A severidade só acende com número: um cartão vermelho marcando zero treina o operador
  // a ignorar vermelho, que é o oposto do que a cor existe para fazer (§3).
  const acesa = indicador.contagem > 0 ? indicador.severidade : "neutro";
  const abre = Boolean(indicador.recorte);

  // **Dinheiro na frente, contagem atrás.** Quando há tarifa, o número grande é o valor —
  // é o que o diretor de arrecadação lê primeiro, e é a razão de a `R-12` existir. A
  // contagem não some: vai na linha de contexto, onde ela responde "quantos trânsitos são
  // esses reais".
  //
  // O valor grande é **compacto** (`R$ 16,6 mi`) e o exato fica no contexto e no `title`.
  // Dezessete caracteres não se leem de três metros, e três metros é a régua do §3.
  const emReais = indicador.unidade === "reais";
  const numero = emReais
    ? reaisCompacto(indicador.valor_centavos)
    : indicador.unidade === "percentual"
      ? percentual(indicador.valor)
      : numeroLongo(indicador.contagem);

  // O denominador vive junto do percentual: "100% de aceitação" sobre dois envios não é a
  // mesma notícia que sobre dois mil. E o valor exato vive junto do compacto, pelo mesmo
  // motivo: o número grande convence, o de baixo é o que se confere.
  const contexto = emReais
    ? `${reais(indicador.valor_centavos)} · ${numeroLongo(indicador.contagem)} trânsitos`
    : indicador.unidade === "percentual"
      ? `${numeroLongo(indicador.contagem)} de ${numeroLongo(indicador.base)} ${indicador.base_rotulo}`
      : indicador.nota;

  const miolo = (
    <>
      {/* O ícone em círculo com a cor do que o número conta — o layout do mockup de
          21/09. Ele dá a cada cartão uma identidade que se reconhece antes de ler. */}
      <span className="flex min-w-0 items-center gap-2">
        <span
          className={`grid size-7 shrink-0 place-items-center rounded-full border ${
            acesa === "critico"
              ? "border-vermelho-borda bg-vermelho-fundo text-vermelho"
              : acesa === "atencao"
                ? "border-amarelo-borda bg-amarelo-fundo text-amarelo"
                : "border-azul-borda bg-azul-fundo text-azul"
          }`}
        >
          {ICONE_DO_INDICADOR[indicador.chave] ?? <Icone.Grafico />}
        </span>
        <span
          className="line-clamp-2 text-[11.5px] leading-tight text-tinta-3"
          title={indicador.rotulo}
        >
          {indicador.rotulo}
        </span>
      </span>
      {/* O número se ajusta à largura do cartão (`cqw`, o cartão é o contêiner): em seis
          colunas ou em três ele cabe numa linha, sem cortar "R$ 14,9 mi" nem estourar a borda. */}
      <span
        className={`mono text-[clamp(20px,13cqw,30px)] leading-none font-extrabold whitespace-nowrap ${
          acesa === "critico"
            ? "text-vermelho"
            : acesa === "atencao"
              ? "text-amarelo"
              : "text-tinta"
        }`}
      >
        {numero}
      </span>

      {contexto && (
        <span
          className="line-clamp-2 text-[10.5px] text-tinta-3"
          title={[contexto, emReais ? indicador.nota : null]
            .filter(Boolean)
            .join(" — ")}
        >
          {contexto}
        </span>
      )}

      {/* `mt-auto` para o convite encostar no rodapé do cartão: sem ele, cada cartão
          termina onde o texto dele acabou, e a fileira de seis vira uma escada. */}
      <span className="mt-auto flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5">
        {/* No cartão, só o selo: o motivo inteiro tem três linhas e mora no painel ao lado,
            onde cabe e onde quem decide vai procurá-lo. Repetido nos dois lugares, ele
            empurra o número — que é a razão de o cartão existir — para o fim da leitura. */}
        {indicador.bloqueio && (
          <span title={indicador.bloqueio}>
            <Badge cor="amarelo">
              {indicador.bloqueio_rotulo ?? "número incompleto"}
            </Badge>
          </span>
        )}
        {abre && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10.5px] font-semibold text-azul">
            Abrir {numeroLongo(indicador.contagem)} na operação
            <Icone.Enviar />
          </span>
        )}
      </span>
    </>
  );

  const forma = `flex h-full w-full min-h-[150px] flex-col items-stretch gap-1.5 rounded-cartao border p-3 text-left ${
    TOM[acesa] ?? TOM.neutro
  }`;

  // Sem fonte, o cartão não abre fila nenhuma — e não vira botão. Um botão que levasse à
  // fila inteira a partir de um número zero seria o AC14 falhando do jeito mais confuso
  // possível para quem está olhando.
  if (!abre) {
    return (
      <div className={forma}>
        {miolo}
        <span className="text-[10px] text-tinta-3">
          Sem fila para abrir enquanto não houver o que contar.
        </span>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={aoAbrir}
      className={`${forma} transition-colors hover:bg-realce`}
    >
      {miolo}
    </button>
  );
}

/**
 * O painel que diz o que a tela **não** está dizendo.
 *
 * Ele existe porque a alternativa é pior: sem ele, um cartão sem valor em reais parece
 * defeito, e a pessoa que decide sobre a tabela de tarifas nunca descobre que é ela quem
 * está no caminho. Aqui a lacuna tem nome, história e dono.
 */
function Pendencias({
  indicadores,
  nota,
}: {
  indicadores: Indicador[];
  nota: string;
}) {
  const bloqueados = indicadores.filter((i) => i.bloqueio);
  if (bloqueados.length === 0) {
    return (
      <Estado tipo="vazio" titulo="Todos os indicadores estão completos." />
    );
  }
  return (
    <>
      {/* Em colunas, e não empilhados: são cinco ou seis cartões curtos, e empilhados eles
          faziam um painel de 600px de altura para dizer o que cabe em duas fileiras. */}
      <div className="grid items-start gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(250px,1fr))]">
        <Cartao titulo="Decisões que faltam" icone={<Icone.Alerta />}>
          {bloqueados.map((i) => (
            <Linha key={i.chave} rotulo={i.rotulo}>
              <span className="text-amarelo">
                {i.bloqueio_rotulo ?? "incompleto"}
              </span>
            </Linha>
          ))}
        </Cartao>
        {bloqueados.map((i) => (
          <Cartao key={i.chave} titulo={i.rotulo}>
            <p className="text-[11.5px] wrap-anywhere text-tinta-3">
              {i.bloqueio}
            </p>
          </Cartao>
        ))}
        {/* O ranking de perdas tem cinco causas, e o cronograma lista outras quatro. A
            diferença precisa estar escrita: um ranking que não diz o que ele não conta é
            lido como a lista completa das causas de perda. */}
        <Cartao
          titulo="Causas de perda que ainda não existem"
          icone={<Icone.Alerta />}
        >
          <p className="text-[11.5px] wrap-anywhere text-tinta-3">{nota}</p>
        </Cartao>
      </div>
      <p className="text-[10.5px] text-tinta-3">
        Última leitura: {dataEHora(new Date().toISOString())}
      </p>
    </>
  );
}

/** As passagens que o pórtico numerou e não chegaram (`SC-04`, ADR-088): fora do ranking em reais —
 * sem a passagem não há categoria, e sem categoria não há tarifa — e sem fila para abrir, porque
 * não são trânsitos. Só aparece quando há alguma. */
function PerdidasNoPortico({ dado }: { dado: NoPortico | null }) {
  if (!dado || (dado.perdidas === 0 && dado.faltam === 0)) return null;
  return (
    <div
      aria-label="Passagens que faltam no pórtico"
      className="mt-2 grid gap-0.5 border-t border-linha pt-2 text-[11.5px]"
    >
      {dado.perdidas > 0 && (
        <p className="text-tinta">
          <b className="text-vermelho">{numeroLongo(dado.perdidas)}</b>{" "}
          {dado.perdidas === 1 ? "passagem perdida" : "passagens perdidas"} no
          pórtico, em {numeroLongo(dado.lacunas_perdidas)}{" "}
          {dado.lacunas_perdidas === 1 ? "lacuna" : "lacunas"} da sequência
        </p>
      )}
      {dado.faltam > 0 && (
        <p className="text-tinta">
          <b className="text-amarelo">{numeroLongo(dado.faltam)}</b> ainda{" "}
          {dado.faltam === 1 ? "falta" : "faltam"}, em{" "}
          {numeroLongo(dado.lacunas_abertas)}{" "}
          {dado.lacunas_abertas === 1 ? "lacuna aberta" : "lacunas abertas"}
        </p>
      )}
      <p className="text-[10.5px] text-tinta-3">{dado.nota}</p>
    </div>
  );
}
